import { doodBlockConfig, propertyValue } from './dood-block-config';
import {
  buildOrderedBlockDescriptor,
  type DescriptorFieldOrderItem,
} from './descriptor-field-order';
import type { IBlockDescriptor } from './descriptor';

export const KIOSKSPLASHSCREENBLOCK_LEGACY_FIELD_ORDER = [
  'label_call_to_action',
  'link_url',
  { type: 'separator' },
  'background_color',
  'background_image',
  'background_video',
  {
    type: 'messageUtil',
    options: {
      type: 'info',
      message: 'pages.blocks.kiosk-splash-screen.messages.video-fallback',
    },
  },
  {
    type: 'messageUtil',
    options: {
      type: 'warn',
      padding: true,
      message: 'pages.blocks.kiosk-splash-screen.messages.video-format',
    },
  },
  'text_color',
  'background_opacity',
  { type: 'separator' },
  'display_accessibility_button',
  'label_accessibility_button',
] as const satisfies readonly DescriptorFieldOrderItem[];

const {
  kioskSplashScreenBlockDescriptor: _generatedKioskSplashScreenBlockDescriptor,
  kioskSplashScreenBlockBlocDefinition,
  kioskSplashScreenBlockDashboardLayout,
  kioskSplashScreenBlockConfig,
  kioskSplashScreenBlockResolveConfig,
  kioskSplashScreenBlockProperties,
} = doodBlockConfig(
  {
    name: 'Kiosk - Splash screen',
    componentType: 'KioskSplashScreenBlockComponent',
    category: 'kiosk',
    description: 'Kiosk - Splash screen.',
    keywords: [],
    sharedFields: {},
  },

  () => ({
    label_call_to_action: propertyValue({
      type: 'text',
      value: '',
      options: {
        label: 'pages.blocks.kiosk-splash-screen.label-call-to-action.label',
      },
    }),
    link_url: propertyValue({
      type: 'text',
      value: '',
      options: {
        label: 'pages.blocks.kiosk-splash-screen.link-url.label',
        help: 'pages.blocks.kiosk-splash-screen.link-url.help',
      },
    }),
    background_color: propertyValue({
      type: 'color',
      value: { type: 'palette', value: 'primary-800' },
      options: {
        label: 'pages.blocks.kiosk-splash-screen.background-color.label',
        help: 'pages.blocks.kiosk-splash-screen.background-color.help',
      },
    }),
    background_image: propertyValue({
      type: 'image',
      value: '',
      options: {
        label: 'pages.blocks.kiosk-splash-screen.background-image.label',
        help: 'pages.blocks.kiosk-splash-screen.background-image.help',
      },
    }),
    background_video: propertyValue({
      type: 'video_file',
      value: '',
      options: {
        label: 'pages.blocks.kiosk-splash-screen.background-video.label',
        help: 'pages.blocks.kiosk-splash-screen.background-video.help',
      },
    }),
    text_color: propertyValue({
      type: 'color',
      value: { type: 'hex', value: '#FFFFFF' },
      options: {
        label: 'pages.blocks.kiosk-splash-screen.text-color.label',
        help: 'pages.blocks.kiosk-splash-screen.text-color.help',
      },
    }),
    background_opacity: propertyValue({
      type: 'select',
      value: 1,
      options: {
        label: 'pages.blocks.kiosk-splash-screen.background-opacity.label',
        help: 'pages.blocks.kiosk-splash-screen.background-opacity.help',
        choices: [
          { label: '0%', value: 0 },
          { label: '10%', value: 0.1 },
          { label: '20%', value: 0.2 },
          { label: '30%', value: 0.3 },
          { label: '40%', value: 0.4 },
          { label: '50%', value: 0.5 },
          { label: '60%', value: 0.6 },
          { label: '70%', value: 0.7 },
          { label: '80%', value: 0.8 },
          { label: '90%', value: 0.9 },
          { label: '100%', value: 1 },
        ],
      },
    }),
    display_accessibility_button: propertyValue({
      type: 'checkbox',
      value: true,
      options: {
        label: 'pages.blocks.kiosk-splash-screen.display-accessibility-button.label',
      },
    }),
    label_accessibility_button: propertyValue({
      type: 'text',
      value: '',
      options: {
        label: 'pages.blocks.kiosk-splash-screen.label-accessibility-button.label',
      },
    }),
  }),

  () => ({
    tabs: [
      {
        label: 'pages.blocks.kiosk-splash-screen.tab.content.label',
        fieldsets: [
          {
            legend: 'pages.blocks.kiosk-splash-screen.fieldset.call-to-action.label',
            fields: ['label_call_to_action', 'link_url', 'text_color'],
          },
          {
            legend: 'pages.blocks.kiosk-splash-screen.fieldset.background.label',
            fields: [
              'background_image',
              'background_video',
              'background_color',
              'background_opacity',
            ],
          },
          {
            legend: 'pages.blocks.kiosk-splash-screen.fieldset.accessibility-button.label',
            fields: [
              'display_accessibility_button',
              {
                prop: 'label_accessibility_button',
                visibleWhen: { display_accessibility_button: true },
              },
            ],
          },
        ],
      },
    ],
  }),
);

export function kioskSplashScreenBlockDescriptor(): IBlockDescriptor;
export function kioskSplashScreenBlockDescriptor(
  fieldOrder: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor;
export function kioskSplashScreenBlockDescriptor(
  fieldOrder?: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor {
  if (fieldOrder === undefined) {
    return _generatedKioskSplashScreenBlockDescriptor;
  }
  return buildOrderedBlockDescriptor(
    _generatedKioskSplashScreenBlockDescriptor,
    kioskSplashScreenBlockProperties,
    fieldOrder,
  );
}

export {
  kioskSplashScreenBlockBlocDefinition,
  kioskSplashScreenBlockDashboardLayout,
  kioskSplashScreenBlockProperties,
  kioskSplashScreenBlockConfig,
  kioskSplashScreenBlockResolveConfig,
};

export type KioskSplashScreenBlockConfig = typeof kioskSplashScreenBlockConfig;
